import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrophy, faMobileScreen, faEnvelope, faCircleCheck, faTriangleExclamation, faSpinner, faBellSlash,
} from "@fortawesome/free-solid-svg-icons";
import ModalHeader from "./ModalHeader";
import api from "../services/api";
import styles from "./ChallengeResultsModal.module.css";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

// Mesmo limite do corpo do push no backend (push_notifications._clip):
// a prévia mostra exatamente o que aparece na notificação.
const PUSH_BODY_MAX = 120;

function clip(text) {
  const t = text.replace(/\s+/g, " ").trim();
  return t.length <= PUSH_BODY_MAX ? t : `${t.slice(0, PUSH_BODY_MAX - 1).trimEnd()}…`;
}

function firstName(name) {
  return (name || "").trim().split(" ")[0] || "Você";
}

/** Espelha challenge_results.render_message do backend, pra prévia. */
export function renderMessage(template, p, winner, challengeTitle) {
  const values = {
    nome: firstName(p.name),
    posicao: String(p.position),
    progresso: p.progress_label,
    vencedor: winner ? firstName(winner.name) : "",
    desafio: challengeTitle || "",
    motivo: p.reason || "",
  };
  return Object.entries(values).reduce((t, [k, v]) => t.split(`{${k}}`).join(v), template).replace(/\s+/g, " ").trim();
}

/** Espelha challenge_results.template_for. */
export function templateKey(p) {
  // A regra mora no backend (challenge_results.template_for): vem pronta no payload
  return p.message_key || "nao_concluiu";
}

/** Espelha challenge_results.render_title: {desafio} no título, até 60 caracteres. */
export function renderTitle(template, challengeTitle) {
  const t = (template || "").split("{desafio}").join(challengeTitle || "").replace(/\s+/g, " ").trim();
  return t.length <= 60 ? t : `${t.slice(0, 59).trimEnd()}…`;
}

function fmtDateTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", dateStyle: "short", timeStyle: "short" });
}

const FIELDS = [
  { key: "vencedor", label: "Para quem venceu" },
  { key: "concluiu", label: "Para quem concluiu" },
  { key: "nao_concluiu", label: "Para quem não concluiu" },
  { key: "nao_concluiu_motivo", label: "Para quem não concluiu e teve treinos que não valeram" },
  { key: "sem_treino", label: "Para quem não treinou" },
  { key: "sem_treino_valido", label: "Para quem treinou, mas nenhum treino valeu" },
];

/**
 * Anunciar o resultado final do desafio aos participantes, em nome do
 * TreinoNexo: push pra quem tem o app com notificação, email pra quem não
 * tem. Uma vez só por desafio. Ver POST /challenges/{id}/results/announce.
 */
export default function ChallengeResultsModal({ open, onClose, challenge }) {
  const challengeId = challenge?.challenge_id ?? challenge?.id;
  useBodyScrollLock(open);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [title, setTitle] = useState("");
  const [templates, setTemplates] = useState({ vencedor: "", concluiu: "", nao_concluiu: "", nao_concluiu_motivo: "", sem_treino: "", sem_treino_valido: "" });
  const [sendEmail, setSendEmail] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open || !challengeId) return;
    setData(null); setLoadError(""); setResult(null); setConfirming(false); setSendError("");
    api.get(`/challenges/${challengeId}/results`)
      .then(({ data: d }) => {
        setData(d);
        setTitle(d.default_title);
        setTemplates(d.default_templates);
      })
      .catch((e) => setLoadError(e?.response?.data?.detail || "Não foi possível carregar o resultado."));
  }, [open, challengeId]);

  const participants = useMemo(() => data?.participants || [], [data]);
  const winner = participants.length ? participants[0] : null;
  const reach = useMemo(() => {
    const push = participants.filter((p) => p.has_device).length;
    const email = participants.filter((p) => !p.has_device && p.can_email).length;
    return { push, email: sendEmail ? email : 0, none: participants.length - push - (sendEmail ? email : 0) };
  }, [participants, sendEmail]);

  // Um exemplo de cada tipo de mensagem que vai sair
  const samples = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const p of participants) {
      const key = templateKey(p);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ p, key, text: renderMessage(templates[key] || "", p, winner, data?.title) });
    }
    return out;
  }, [participants, winner, templates, data]);

  const canSend = data && data.ended && !data.results_announced_at && participants.length
    && title.trim() && FIELDS.every((f) => templates[f.key]?.trim());

  const send = async () => {
    setSending(true); setSendError("");
    try {
      const { data: r } = await api.post(`/challenges/${challengeId}/results/announce`, {
        title: title.trim(), templates, send_email: sendEmail,
      });
      setResult(r);
      setData((d) => ({ ...d, results_announced_at: r.results_announced_at }));
    } catch (e) {
      setSendError(e?.response?.data?.detail || "Não foi possível enviar o anúncio.");
    } finally {
      setSending(false); setConfirming(false);
    }
  };

  if (!open) return null;

  const content = (
    <div className={styles.overlay} onClick={sending ? undefined : onClose}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="results-title" onClick={(e) => e.stopPropagation()}>
        <ModalHeader onClose={onClose} disabled={sending} />
        <div className={styles.header}>
          <h2 id="results-title" className={styles.title}>
            <FontAwesomeIcon icon={faTrophy} aria-hidden="true" /> Anunciar resultado
          </h2>
          <p className={styles.sub}>{challenge?.title}</p>
        </div>

        <div className={styles.body}>
          {!data && !loadError && <p className={styles.muted}><FontAwesomeIcon icon={faSpinner} spin /> Carregando resultado...</p>}
          {loadError && <p className={styles.error} role="alert">{loadError}</p>}

          {data && (
            <>
              {data.results_announced_at && (
                <p className={styles.done} role="status">
                  <FontAwesomeIcon icon={faCircleCheck} aria-hidden="true" /> Resultado anunciado em {fmtDateTime(data.results_announced_at)}.
                </p>
              )}
              {!data.ended && (
                <p className={styles.warn}>O último dia do desafio é {data.end_date.split("-").reverse().join("/")}. O anúncio libera no dia seguinte, quando todos já tiveram o dia inteiro para treinar.</p>
              )}

              <section>
                <h3 className={styles.sectionTitle}>Classificação final</h3>
                {!data.anyone_completed && participants.length > 0 && (
                  <p className={styles.muted}>Ninguém concluiu a meta: a ordem é pelo progresso, e "{"{vencedor}"}" vira quem terminou na frente.</p>
                )}
                <ol className={styles.ranking}>
                  {participants.map((p) => (
                    <li key={p.user_id} className={styles.rankRow}>
                      <span className={styles.pos}>{p.position}º</span>
                      <span className={styles.rankMain}>
                        <span className={styles.name}>{p.name}</span>
                        <span className={styles.meta}>
                          {p.progress_label}{p.completed_at ? ` · concluiu em ${fmtDateTime(p.completed_at)}` : ""}
                          {!p.completed && p.invalid_count > 0
                            ? ` · ${p.invalid_count} ${p.invalid_count === 1 ? "registro não valeu" : "registros não valeram"}${p.reason ? ` (${p.reason})` : ""}`
                            : ""}
                        </span>
                      </span>
                      <span className={styles.channel} title={p.has_device ? "Recebe notificação" : p.can_email ? "Recebe por email" : "Sem notificação nem email"}>
                        <FontAwesomeIcon icon={p.has_device ? faMobileScreen : p.can_email ? faEnvelope : faBellSlash} aria-hidden="true" />
                        <span className={styles.srOnly}>{p.has_device ? "notificação" : p.can_email ? "email" : "sem contato"}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              </section>

              {!data.results_announced_at && data.ended && participants.length > 0 && !result && (
                <>
                  <section className={styles.form}>
                    <h3 className={styles.sectionTitle}>Mensagem</h3>
                    <label className={styles.label} htmlFor="results-push-title">Título da notificação ({"{desafio}"} vira o nome do desafio)</label>
                    <input id="results-push-title" className={styles.input} value={title} maxLength={80}
                           onChange={(e) => setTitle(e.target.value)} />
                    {FIELDS.map((f) => (
                      <div key={f.key} className={styles.field}>
                        <label className={styles.label} htmlFor={`tpl-${f.key}`}>{f.label}</label>
                        <textarea id={`tpl-${f.key}`} className={styles.textarea} rows={2} maxLength={300}
                                  value={templates[f.key]} onChange={(e) => setTemplates((t) => ({ ...t, [f.key]: e.target.value }))} />
                      </div>
                    ))}
                    <p className={styles.muted}>
                      Variáveis: {"{nome}"}, {"{posicao}"}, {"{progresso}"}, {"{vencedor}"}, {"{desafio}"}, {"{motivo}"}. Na notificação aparecem até {PUSH_BODY_MAX} caracteres; no email vai o texto inteiro.
                    </p>
                  </section>

                  <section>
                    <h3 className={styles.sectionTitle}>Prévia</h3>
                    <ul className={styles.previews}>
                      {samples.map(({ p, key, text }) => (
                        <li key={key} className={styles.preview}>
                          <span className={styles.previewTitle}>{renderTitle(title, data?.title) || " "}</span>
                          <span className={styles.previewBody}>{p.has_device ? clip(text) : text}</span>
                          <span className={styles.previewFor}>{p.name} · {FIELDS.find((f) => f.key === key)?.label.toLowerCase()}</span>
                        </li>
                      ))}
                    </ul>
                  </section>

                  <section className={styles.reach}>
                    <p>
                      <FontAwesomeIcon icon={faMobileScreen} aria-hidden="true" /> {reach.push} por notificação
                      {" · "}<FontAwesomeIcon icon={faEnvelope} aria-hidden="true" /> {reach.email} por email
                      {reach.none > 0 && <> · {reach.none} sem contato</>}
                    </p>
                    <label className={styles.check}>
                      <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
                      <span>Mandar por email para quem não tem o app com notificação</span>
                    </label>
                  </section>

                  {sendError && <p className={styles.error} role="alert"><FontAwesomeIcon icon={faTriangleExclamation} /> {sendError}</p>}

                  {confirming ? (
                    <div className={styles.confirm} role="alert">
                      <p>Enviar o resultado para {reach.push + reach.email} {reach.push + reach.email === 1 ? "pessoa" : "pessoas"} agora? Não dá para enviar de novo depois.</p>
                      <div className={styles.actions}>
                        <button type="button" className={styles.ghost} onClick={() => setConfirming(false)} disabled={sending}>Cancelar</button>
                        <button type="button" className={styles.primary} onClick={send} disabled={sending}>
                          {sending ? "Enviando..." : "Sim, enviar"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className={styles.primary} onClick={() => setConfirming(true)} disabled={!canSend}>
                      Anunciar para {participants.length} {participants.length === 1 ? "participante" : "participantes"}
                    </button>
                  )}
                </>
              )}

              {result && (
                <p className={styles.done} role="status">
                  <FontAwesomeIcon icon={faCircleCheck} aria-hidden="true" /> Enviado: {result.pushed} por notificação
                  {result.emailed > 0 && `, ${result.emailed} por email`}
                  {result.push_failed > 0 && ` (${result.push_failed} notificação(ões) falharam${sendEmail ? " e foram por email" : ""})`}
                  {result.unreachable > 0 && `. ${result.unreachable} sem contato.`}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
